import React, { useState } from 'react';
import useIsMobile from '../useIsMobile';

const st = {
  colTitle: { fontSize: 12, fontWeight: 600, color: '#999', textTransform: 'uppercase', marginBottom: 8, letterSpacing: '0.06em' },
  list: { background: '#111', borderRadius: 6, padding: 8, display: 'flex', flexDirection: 'column', gap: 4 },
  item: {
    minHeight: 44,
    padding: '8px 12px',
    background: '#1a1a1a',
    borderRadius: 4,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    fontSize: 14,
    userSelect: 'none',
    transition: 'background 0.1s',
  },
  itemDragging: { opacity: 0.4 },
  itemOver: { outline: '1px dashed rgba(249,176,0,0.7)', outlineOffset: '-1px' },
  pos: {
    width: 32,
    textAlign: 'right',
    color: '#f9b000',
    fontWeight: 700,
    fontSize: 13,
    flexShrink: 0,
    fontFamily: 'var(--font-mono)',
  },
  acronym: {
    fontWeight: 700,
    fontFamily: 'var(--font-mono)',
    letterSpacing: '0.04em',
    fontSize: 13,
  },
  tag: { fontSize: 11, color: '#666', marginLeft: 'auto' },
  arrowBtn: {
    background: '#333', border: 'none', color: '#ccc', borderRadius: 3,
    width: 28, height: 28, cursor: 'pointer', fontSize: 14, flexShrink: 0,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  },
};

function teamBorderStyle(teamColour) {
  return { borderLeft: `4px solid ${teamColour ? '#' + teamColour : '#444'}` };
}

function AvailableList({ available, onAdd, orderLabel }) {
  return (
    <div>
      <div style={st.colTitle}>Available Drivers ({available.length})</div>
      {orderLabel && <div style={{ fontSize: 12, color: '#999', marginBottom: 8 }}>{orderLabel}</div>}
      <div style={st.list}>
        {available.map(d => (
          <div
            key={d.acronym}
            style={{ ...st.item, ...teamBorderStyle(d.teamColour) }}
            onClick={() => onAdd(d.acronym)}
          >
            <span style={st.acronym}>{d.acronym}</span>
            <span style={{ color: '#aaa' }}>{d.fullName}</span>
            <span style={st.tag}>{d.team}</span>
          </div>
        ))}
        {available.length === 0 && (
          <p style={{ color: '#666', padding: 8, fontSize: 13 }}>All drivers selected</p>
        )}
      </div>
    </div>
  );
}

function SelectedListDesktop({ selected, value, dragIdx, overIdx, onRemove, onDragStart, onDragOver, onDrop, onDragEnd }) {
  return (
    <div>
      <div style={st.colTitle}>Your Top 10 ({value.length}/10)</div>
      <div style={{ ...st.list, minHeight: 300 }}>
        {selected.map((d, idx) => (
          <div
            key={d.acronym}
            style={{
              ...st.item,
              ...teamBorderStyle(d.teamColour),
              ...(dragIdx === idx ? st.itemDragging : {}),
              ...(overIdx === idx && dragIdx !== idx ? st.itemOver : {}),
            }}
            draggable
            onDragStart={e => onDragStart(e, idx)}
            onDragOver={e => onDragOver(e, idx)}
            onDrop={e => onDrop(e, idx)}
            onDragEnd={onDragEnd}
            onClick={() => onRemove(idx)}
            title="Click to remove, drag to reorder"
          >
            <span style={st.pos}>P{idx + 1}</span>
            <span style={st.acronym}>{d.acronym}</span>
            <span style={{ color: '#aaa' }}>{d.fullName}</span>
          </div>
        ))}
        {value.length === 0 && (
          <p style={{ color: '#666', padding: 8, fontSize: 13 }}>Click drivers to add them</p>
        )}
      </div>
    </div>
  );
}

function SelectedListMobile({ selected, value, onRemove, onMoveUp, onMoveDown }) {
  return (
    <div style={{ marginBottom: 16 }}>
      <div style={st.colTitle}>Your Top 10 ({value.length}/10)</div>
      <div style={st.list}>
        {selected.map((d, idx) => (
          <div key={d.acronym} style={{ ...st.item, cursor: 'default', ...teamBorderStyle(d.teamColour) }}>
            <span style={st.pos}>P{idx + 1}</span>
            <span style={st.acronym}>{d.acronym}</span>
            <span style={{ color: '#aaa', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {d.fullName}
            </span>
            <div style={{ display: 'flex', gap: 4, marginLeft: 4 }}>
              <button type="button" style={st.arrowBtn} onClick={() => onMoveUp(idx)} disabled={idx === 0}>↑</button>
              <button type="button" style={st.arrowBtn} onClick={() => onMoveDown(idx)} disabled={idx === selected.length - 1}>↓</button>
              <button type="button" style={{ ...st.arrowBtn, color: '#f9b000' }} onClick={() => onRemove(idx)}>×</button>
            </div>
          </div>
        ))}
        {value.length === 0 && (
          <p style={{ color: '#666', padding: 8, fontSize: 13 }}>Tap drivers below to add them</p>
        )}
      </div>
    </div>
  );
}

export default function DriverSelect({ drivers, value, onChange, qualifyingGrid = [], startingGrid = [] }) {
  const isMobile = useIsMobile();
  const [dragIdx, setDragIdx] = useState(null);
  const [overIdx, setOverIdx] = useState(null);

  const qualifyingPositions = new Map();
  for (const row of qualifyingGrid) {
    if (row.code && Number.isInteger(row.position) && row.position > 0)
      qualifyingPositions.set(row.code, row.position);
  }
  const qualifyingOrder = drivers.some(d => qualifyingPositions.has(d.acronym));
  const startingPositions = new Map();
  for (const row of startingGrid) {
    if (Number.isInteger(row.driverNumber) && Number.isInteger(row.position) && row.position >= 0)
      startingPositions.set(row.driverNumber, row.position || Infinity);
  }
  const startingOrder = drivers.some(d => startingPositions.has(d.number));
  const orderLabel = startingOrder ? 'Starting grid order' : qualifyingOrder ? 'Qualifying order' : null;
  const positionFor = driver => startingOrder
    ? startingPositions.get(driver.number) ?? Infinity : qualifyingPositions.get(driver.acronym) ?? Infinity;
  const available = drivers.filter(d => !value.includes(d.acronym)).sort((a, b) => {
    const aPosition = positionFor(a);
    const bPosition = positionFor(b);
    return (aPosition === bPosition ? 0 : aPosition - bPosition) || a.acronym.localeCompare(b.acronym);
  });
  const selected = value.map(acr => drivers.find(d => d.acronym === acr)).filter(Boolean);

  const addDriver = (acronym) => {
    if (value.length < 10) onChange([...value, acronym]);
  };

  const removeDriver = (idx) => {
    const next = [...value];
    next.splice(idx, 1);
    onChange(next);
  };

  const moveUp = (idx) => {
    if (idx === 0) return;
    const next = [...value];
    [next[idx - 1], next[idx]] = [next[idx], next[idx - 1]];
    onChange(next);
  };

  const moveDown = (idx) => {
    if (idx === value.length - 1) return;
    const next = [...value];
    [next[idx], next[idx + 1]] = [next[idx + 1], next[idx]];
    onChange(next);
  };

  const handleDragStart = (e, idx) => { setDragIdx(idx); e.dataTransfer.effectAllowed = 'move'; };
  const handleDragOver = (e, idx) => { e.preventDefault(); setOverIdx(idx); };
  const handleDrop = (e, idx) => {
    e.preventDefault();
    if (dragIdx === null || dragIdx === idx) return;
    const next = [...value];
    const [moved] = next.splice(dragIdx, 1);
    next.splice(idx, 0, moved);
    onChange(next);
    setDragIdx(null); setOverIdx(null);
  };
  const handleDragEnd = () => { setDragIdx(null); setOverIdx(null); };

  if (isMobile) {
    return (
      <div style={{ marginTop: 12 }}>
        <SelectedListMobile
          selected={selected}
          value={value}
          onRemove={removeDriver}
          onMoveUp={moveUp}
          onMoveDown={moveDown}
        />
        <AvailableList available={available} onAdd={addDriver} orderLabel={orderLabel} />
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', gap: 16, marginTop: 12 }}>
      <div style={{ flex: 1 }}>
        <AvailableList available={available} onAdd={addDriver} orderLabel={orderLabel} />
      </div>
      <div style={{ flex: 1 }}>
        <SelectedListDesktop
          selected={selected}
          value={value}
          dragIdx={dragIdx}
          overIdx={overIdx}
          onRemove={removeDriver}
          onDragStart={handleDragStart}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
          onDragEnd={handleDragEnd}
        />
      </div>
    </div>
  );
}
